import React, { useState, useEffect, useRef, useCallback } from "react";
import { Constants } from "../../constants/appConstants";
import { Order } from "../../models/csos/order";
import { dateService } from "../../services/dateService";
import { userService } from "../../services/userService";
import { useOrderDetailsService } from "../../services/component/orderDetailsService";
import { formatCurrency } from "../../utils/formatCurrency";
import UTNUpdate from "./updateUTN/UTNUpdate";
import "./OrderDetailHeader.css";

interface OrderDetailHeaderProps {
  order: any;
  orderlockedtime: any;
}

export const calculateTimeElapsed = (currentTime: Date, orderlockedtime: any) => {
  if (!orderlockedtime) {
    return { minutesLapsed: 0, secondsDifference: 0, secondsLapsed: 0 };
  }

  const localizedDate = dateService.localizeDate(orderlockedtime, "MM/DD/YYYY hh:mm:ss A", "MM/DD/YYYY HH:mm:ss");
  const lockOrderStp = new Date(localizedDate);
  const minuteLocked = lockOrderStp.getMinutes();
  const secondsLocked = lockOrderStp.getSeconds();
  const currentSec = currentTime.getSeconds();
  const currentMin = currentTime.getMinutes();
  let minsLapsed: number;
  if (currentMin < minuteLocked) {
    minsLapsed = (currentMin - minuteLocked) + 60;
  } else {
    minsLapsed = currentMin - minuteLocked;
  }
  let secsDiff: number;
  if (currentSec < secondsLocked) {
    secsDiff = (currentSec - secondsLocked) + 60;
    minsLapsed = minsLapsed - 1;
  } else {
    secsDiff = currentSec - secondsLocked;
  }
  return { minutesLapsed: minsLapsed, secondsDifference: secsDiff, secondsLapsed: minsLapsed * 60 + secsDiff };
};

export const getTimerCountdown = (newStartTime: number, totalSecondsLapsed: number) => {
  const currentTime = new Date().getTime();
  const diff = currentTime - newStartTime;
  const secsLeft = Constants.TIMER_SECONDS - Math.floor(diff / 1000) - totalSecondsLapsed;

  if (secsLeft >= 0) {
    const m = Math.floor(secsLeft / 60);
    const s = secsLeft % 60;
    const mStr = m < 10 ? `0${m}` : `${m}`;
    const sStr = s < 10 ? `0${s}` : `${s}`;
    return { expired: false, minutes: m, seconds: s, time: ` ${mStr}:${sStr}` };
  }

  return { expired: true, minutes: 0, seconds: 0, time: " 00: 00" };
};

const OrderDetailHeader: React.FC<OrderDetailHeaderProps> = ({ order, orderlockedtime }) => {
  const orderDetailsService = useOrderDetailsService();
  const [loggedInUser] = useState(userService.getCurrentUser());
  const [time, setTime] = useState("");
  const [minutes, setMinutes] = useState(14);
  const [seconds, setSeconds] = useState(60);
  const [utnPrefix, setUtnPrefix] = useState("");
  const [utnSuffix, setUtnSuffix] = useState("");
  const [showUtnLayer, setShowUtnLayer] = useState(false);
  const intervalsRef = useRef<number[]>([]);
  const counterRef = useRef(1);

  const clearAllIntervals = useCallback(() => {
    intervalsRef.current.forEach((id) => clearInterval(id));
    intervalsRef.current = [];
  }, []);

  const errorModal = useCallback(() => {
    setMinutes(0);
    setSeconds(0);
    setTime(" 00: 00");
    orderDetailsService.showTimerModal("show");
  }, [orderDetailsService]);

  const newTimer = useCallback((newStartTime: number, totalSecondsLapsed: number) => {
    const countdown = getTimerCountdown(newStartTime, totalSecondsLapsed);
    if (countdown.expired) {
      clearAllIntervals();
      errorModal();
      return;
    }

    setMinutes(countdown.minutes);
    setSeconds(countdown.seconds);
    setTime(countdown.time);
  }, [clearAllIntervals, errorModal]);

  const startTimer = useCallback((newStartTime: number, totalSecondsLapsed: number) => {
    newTimer(newStartTime, totalSecondsLapsed);
    const x = window.setInterval(() => {
      newTimer(newStartTime, totalSecondsLapsed);
    }, 990);
    intervalsRef.current.push(x);
  }, [newTimer]);

  const timerFunction = useCallback((minutesLapsed: number, secondLapsed: number) => {
    if (counterRef.current < 10) {
      const newStartTime = new Date().getTime();
      const totalSecondsLapsed = 60 * minutesLapsed + secondLapsed;
      startTimer(newStartTime, totalSecondsLapsed);
    } else {
      counterRef.current++;
    }
  }, [startTimer]);

  const timeLapsedCalculation = useCallback((currentTime: Date) => calculateTimeElapsed(currentTime, orderlockedtime), [orderlockedtime]);

  useEffect(() => {
    const unsub1 = orderDetailsService.announcestartTimer$.subscribe((orderdetail: Order) => {
      if (orderdetail) {
        setTime("");
        const currentTime = new Date();
        const { minutesLapsed, secondsDifference, secondsLapsed: secsLapsed } = timeLapsedCalculation(currentTime);
        if (orderdetail.statusDesc === "New" && !orderdetail.orderLocked) {
          timerFunction(0, 0);
        } else if (orderdetail.statusDesc === "New" && orderdetail.orderLocked &&
          loggedInUser === orderdetail.lastUpdatedUser && secsLapsed < Constants.TIMER_SECONDS) {
          setMinutes((previousMinutes) => previousMinutes - minutesLapsed);
          setSeconds((previousSeconds) => previousSeconds - secondsDifference);
          timerFunction(0, secsLapsed);
        } else if (orderdetail.statusDesc === "New" && orderdetail.orderLocked &&
          loggedInUser === orderdetail.lastUpdatedUser) {
          timerFunction(0, 0);
        }
      }
    });
    const unsub2 = orderDetailsService.resetTime$.subscribe((reset) => {
      if (reset) {
        clearAllIntervals();
        setMinutes(0);
        setSeconds(0);
      }
    });
    return () => { unsub1(); unsub2(); };
  }, [orderDetailsService, loggedInUser, clearAllIntervals, timeLapsedCalculation, timerFunction]);

  useEffect(() => {
    if (!order || order.status !== Constants.NEW) {
      return;
    }

    if (!order.orderLocked) {
      clearAllIntervals();
      startTimer(new Date().getTime(), 0);
      return;
    }

    if (loggedInUser === order.updatedBy && orderlockedtime) {
      clearAllIntervals();
      const currentTime = new Date();
      const { minutesLapsed, secondsDifference, secondsLapsed: secsLapsed } = timeLapsedCalculation(currentTime);
      if (secsLapsed < Constants.TIMER_SECONDS) {
        setMinutes((previousMinutes) => previousMinutes - minutesLapsed);
        setSeconds((previousSeconds) => previousSeconds - secondsDifference);
        timerFunction(0, secsLapsed);
      } else {
        timerFunction(0, 0);
      }
    }
  }, [clearAllIntervals, loggedInUser, order, orderlockedtime, startTimer, timeLapsedCalculation, timerFunction]);

  const splitUtn = () => {
    if (order.uniqueTrackingNum) {
      setUtnPrefix(order.uniqueTrackingNum.slice(0, 3));
      setUtnSuffix(order.uniqueTrackingNum.slice(3));
      setShowUtnLayer(true);
    }
  };

  return (
    <div className="modal-header">
      <div className="bgred">
        <div className="orderdetail">CSOS Order Details</div>
      </div>
      <div className="col-md-12 col-sm-12 order-head">
        <div className="col-md-4 col-sm-4">
          <h5 className="customer-name">{order.customerName}</h5>
        </div>
        <div className="col-md-4 col-sm-4">
          {(order.status === Constants.INPROGRESS || order.status === Constants.SIGNED) && (
            <h5>Signed By: <span className="signedby">{order.updatedBy}</span></h5>
          )}
          {order.status === Constants.APPROVED && (
            <h5>Approved By: <span className="approvedby">{order.updatedBy}</span></h5>
          )}
          {order.orderLocked && loggedInUser !== order.updatedBy && (
            <h5>Locked By: <span className="lockedby">{order.updatedBy}</span></h5>
          )}
          {((order.status === Constants.NEW && !order.orderLocked) ||
            (order.status === Constants.NEW && order.orderLocked && loggedInUser === order.updatedBy)) && (
            <h5>Time Remaining: <span className="highlight">{time}</span></h5>
          )}
          <h5>
            UTN: <span className="uniquetrackingnum">{order.uniqueTrackingNum} </span>
            {(order.status === Constants.NEW && (!order.orderLocked || (order.orderLocked && loggedInUser === order.updatedBy))) && (
              <a href="#" className="edit-icon" onClick={(e) => { e.preventDefault(); splitUtn(); }}>
                <span className="editPencil"></span>
              </a>
            )}
          </h5>
          {showUtnLayer && (
            <UTNUpdate order={order} utnPrefix={utnPrefix} utnSuffix={utnSuffix} onClose={() => setShowUtnLayer(false)} />
          )}
        </div>
        <div className="col-md-4 col-sm-4 approve-order" style={{ display: order.status === "11" ? "block" : "none" }}>
          <h5>Approved Orders: {order.totalNumOfApprovedOrders}</h5>
        </div>
      </div>
      <div className="col-md-12 col-sm-12 desc-head" style={{ backgroundImage: "url('assets/images/CAH_Accordian_Expanded.png')", backgroundRepeat: "no-repeat", backgroundSize: "100%" }}>
        <div className="col-md-4 col-sm-4 purchaseorderid">PO #: {order.purchaseOrderId}</div>
        <div className="col-md-4 col-sm-4"><span className="nooflines">{order.noOfLines} Lines</span></div>
        <div className="col-md-4 col-sm-4 totalcost">Total Cost: {formatCurrency(order.totalCost)}</div>
      </div>
      <div className="desc-container">
        <div className="row">
          <div className="col-md-12 col-sm-12 desc-row">
            <div className="col-md-4 col-sm-4">
              <table className="table3">
                <tbody>
                  <tr><td>Order #:</td><td>{order.orderId}</td></tr>
                  <tr><td>Order Date:</td><td>{dateService.localizeDate(order.submitted, "MM/DD/YYYY hh:mm A", "MM/DD/YYYY hh:mm A")}</td></tr>
                  <tr><td>Submitted By:</td><td>{order.submittedBy}</td></tr>
                </tbody>
              </table>
            </div>
            <div className="col-md-4 col-sm-4">
              <table className="table3">
                <tbody>
                  <tr><td className="DEAregistration">DEA Registration #:</td><td className="DEAvalue">{order.deaLicenseId}</td></tr>
                  <tr><td>Customer #:</td><td>{order.customerNum}</td></tr>
                  <tr><td>Customer Name:</td><td>{order.customerName}</td></tr>
                  <tr className="customer-address"><td>Customer Address:</td><td>{order.customerAddr}{order.customerAddr2}<br />{order.customerCity}, {order.customerState} {order.customerPostalCode}</td></tr>
                </tbody>
              </table>
            </div>
            <div className="col-md-4 col-sm-4">
              <table className="table3">
                <tbody>
                  <tr>
                    <td>Order Status:</td>
                    {order.orderLocked && order.statusDesc === "New" && loggedInUser !== order.updatedBy ? (
                      <td>Locked</td>
                    ) : (
                      <td>{order.statusDesc}</td>
                    )}
                  </tr>
                  <tr><td>DC Name:</td><td>{order.dcName}</td></tr>
                  <tr className="DC-address"><td>DC Address:</td><td>{order.dcAddress}{order.dcAddress2}<br />{order.dcCity}, {order.dcState} {order.dcPostalCode}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailHeader;
